import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.4, delay: i * 0.1 } }),
};

export default function WhyDustMattersPage() {
  return (
    <main className="pt-16">
      {/* Header */}
      <section data-testid="why-dust-hero" className="relative py-20 md:py-32 border-b border-[#2d3239] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1714951823702-c6fb2f91f3f9?w=1920&q=50"
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-[0.08]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0b0d]/80 to-[#0a0b0d]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">Research Brief</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight uppercase text-[#f2f4f8] leading-[1.05] mb-6">
              Why Lunar Dust Matters
            </h1>
            <p className="text-base md:text-lg text-[#8c9eb3] leading-relaxed max-w-2xl">
              A systems-engineering perspective on the dust ingress problem for lunar habitable volumes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Problem */}
      <section data-testid="why-dust-problem" className="py-20 md:py-32 border-b border-[#2d3239]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#d97706] mb-4">The Problem</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#f2f4f8] mb-8">
              Lunar Dust Is Not Ordinary Dust
            </h2>
            {/* Regolith close-up image */}
            <div className="relative w-full h-48 md:h-64 mb-10 border border-[#2d3239] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1470002470818-ac0a29bc6496?w=1200&q=60"
                alt="Lunar regolith surface texture"
                className="w-full h-full object-cover opacity-60"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0b0d] via-transparent to-[#0a0b0d]/40" />
              <div className="absolute bottom-4 left-4">
                <p className="font-mono text-[10px] tracking-widest uppercase text-[#8c9eb3]">Regolith surface — sub-micron angular particles</p>
              </div>
            </div>
            <div className="space-y-6 text-base text-[#8c9eb3] leading-relaxed">
              <p>
                Lunar regolith is fine-grained, angular, and chemically reactive. Unlike terrestrial dust, lunar particles have not been weathered by water or atmosphere. They retain sharp, fractured edges at the sub-micron scale, making them mechanically abrasive and biologically persistent.
              </p>
              <p>
                Apollo crew members reported respiratory irritation after lunar dust exposure inside the Lunar Module. Post-mission analysis confirmed that respirable particles — those small enough to penetrate deep into the lungs — were present in significant concentrations after EVA return.
              </p>
              <p>
                For Artemis-era missions involving longer surface stays, repeated EVA cycles, and larger habitable volumes, the exposure profile is qualitatively different. Dust is not a temporary inconvenience. It is a cumulative crew-health and systems-integrity challenge.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Requirement */}
      <section data-testid="why-dust-requirement" className="py-20 md:py-32 border-b border-[#2d3239]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">The Requirement</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#f2f4f8] mb-8">
              NASA Has Defined the Problem
            </h2>
            <div className="space-y-6 text-base text-[#8c9eb3] leading-relaxed">
              <p>
                NASA-STD-3001, the agency's human-systems integration standard, sets a lunar dust contamination limit for habitable atmospheres. This is not guidance — it is a requirement that lunar surface systems must meet.
              </p>
              <p>
                The supporting technical brief (OCHMO-TB-011) goes further, calling for monitoring of dust levels, crew alerting, and operational controls including airlock separation and cabin clearing protocols. The brief explicitly links dust contamination to crew health risk and system degradation.
              </p>
              <p>
                NASA's 2024 Lunar Dust Mitigation Roadmap includes a "Cabin Dust Monitor" capability for habitable environments, reinforcing that interior-side dust monitoring is an identified mission need — not a speculative product category.
              </p>
            </div>
            <div className="mt-8 space-y-3">
              {[
                "NASA-STD-3001 Vol 2 — Human Factors, Habitability, and Environmental Health",
                "OCHMO-TB-011 — Lunar Dust Technical Brief",
                "NASA Lunar Dust Mitigation Roadmap (Fall 2024)",
              ].map((ref) => (
                <div key={ref} className="border-l-2 border-[#2d3239] pl-4">
                  <p className="font-mono text-xs text-[#8c9eb3]">{ref}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Mission Implication */}
      <section data-testid="why-dust-implication" className="py-20 md:py-32 border-b border-[#2d3239]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">Mission Implication</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#f2f4f8] mb-8">
              Habitats and Rovers Need Ingress Management
            </h2>
            <div className="space-y-6 text-base text-[#8c9eb3] leading-relaxed">
              <p>
                Fixed habitats will experience repeated dust ingress from airlock cycling and suit maintenance. Pressurized rovers — which NASA describes as mobile habitats for extended traverses — face additional ingress vectors from docking, undocking, and surface-contact operations.
              </p>
              <p>
                In both cases, the requirement is not simply to sense dust after the fact. It is to detect ingress events as they happen, classify them against the current operational state, drive timely containment actions, and maintain a cumulative exposure record for crew health monitoring.
              </p>
              <p>
                This is a systems problem. It requires a subsystem — not a standalone sensor, not a coating, and not a cleaning robot.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The System Implication */}
      <section data-testid="why-dust-system" className="py-20 md:py-32 border-b border-[#2d3239]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">System Implication</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#f2f4f8] mb-8">
              More Than a Standalone Sensor
            </h2>
            <div className="space-y-6 text-base text-[#8c9eb3] leading-relaxed">
              <p>
                A single particle counter does not solve the dust ingress problem. It can report a number, but it cannot distinguish between a normal background reading and an active ingress event. It cannot correlate with mission state. It cannot drive a containment response. It cannot maintain a crew exposure record.
              </p>
              <p>
                What is needed is a distributed sensing network with event inference, operational-state context, bounded response logic, and audit-grade exposure tracking. That is the system that DustSentinel-LX provides.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="text-base text-[#8c9eb3] mb-6">
              Technical overview and standards-alignment summary available on request.
            </p>
            <Link
              to="/contact"
              data-testid="why-dust-cta-button"
              className="bg-[#4b7399] text-[#f2f4f8] font-mono text-sm tracking-wider uppercase px-8 py-3 rounded-none hover:bg-[#3a5a78] transition-colors inline-flex items-center gap-2"
            >
              Request Technical Overview <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
